import React from 'react';
import { Location, useLocation } from 'react-router-dom';

import { GenericError } from '~/components/GenericError';
import { ResponseError } from '~/domain/ResponseError';

interface IErrorBoundary {
  location: Location;
  dialog?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

interface IErrorBoundaryState {
  error: Error | null;
}

export const ErrorBoundary: React.FC<Omit<IErrorBoundary, 'location'>> = (
  props
) => {
  const { children, ...others } = props;
  const location = useLocation();
  return (
    <Boundary location={location} {...others}>
      {children}
    </Boundary>
  );
};

class Boundary extends React.PureComponent<IErrorBoundary, IErrorBoundaryState> {
  constructor(props: IErrorBoundary) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error | ResponseError) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  // clear error when user navigates to a different page
  componentDidUpdate(prevProps: IErrorBoundary) {
    if (prevProps.location !== this.props.location) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    const { children, dialog, onClose, ...others } = this.props;
    const handleClose = () => {
      this.setState({ error: null });
      onClose && onClose();
    };
    return (
      <>
        {!error && children}
        {error && (<GenericError error={error} dialog={dialog} onClose={handleClose} {...others} />)}
      </>
    );
  }
}
